import {
  BriefcaseMedical,
  Check,
  ChevronDown,
  DollarSign,
  ListChecks,
  Stethoscope,
  User,
  Users,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { CircleLoader } from 'common-src/components/base';
import { getClients } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { ColorsNew } from 'common-src/styles';

import useCustomSelector from 'src/hooks/useCustomSelector';

import { balanceAssign, computeMinutesLoadByClientId } from '../../balanceAssign';
import {
  DEFAULT_OWNER_STATS,
  DEFAULT_STATS,
  pathwayLabel,
  TASK_QUEUE_OWNER_TABS,
  TASK_QUEUE_SORT_MODE,
  TASK_QUEUE_SORT_MODE_OPTIONS,
  TASK_QUEUE_STATUS_TABS,
  TASK_QUEUE_TEAM_TABS,
  TASK_STATUS_FILTER_KEYS,
} from '../../constants';
import {
  buildPatientGroups,
  buildPatientSearchMetadataById,
  buildPriorityGroups,
  buildReasonGroups,
  buildRenderableSectionItems,
  buildRenderableSectionTasks,
  buildTaskQueuePrioritySummary,
  buildTaskQueueSummaryLabel,
  buildTaskSearchOptions,
  getDelegatedSubtaskMeta,
  getLinkedTaskWaitingForTitle,
  getPatientSearchPathway,
  getTaskSearchPlaceholder,
  getTaskSearchSelectionLabel,
} from '../../helpers';
import { Badge } from '../Badge';
import BulkAssignPreview from '../BulkAssignPreview';
import PatientTaskSearch from '../PatientTaskSearch/PatientTaskSearch';
import ScopeFilterGroup from '../ScopeFilterGroup';
import TaskRow from '../TaskRow';
import taskViewStyles from '../TasksView/TasksView.module.scss';
import styles from './BulkAssignView.module.scss';

const TEAM_TAB_ICON_BY_KEY = {
  all: Users,
  member: User,
  ops: BriefcaseMedical,
  clinical: Stethoscope,
  rcm: DollarSign,
};

const BulkAssignView = ({
  isLoading,
  departments = [],
  scopeFilters,
  onChangeScopeFilters,
  guideEnabled = false,
  onToggleGuide,
  isScopePaused = false,
  onToggleScopePause,
  isPinnedToCurrentScope = false,
  canResetToPinnedScope = false,
  showScopeUtilities = false,
  onToggleScopePin,
  onResetScopeToPinned,
  ownerFilter,
  setOwnerFilter,
  ownerStats,
  activeTeam,
  setActiveTeam,
  statusFilter,
  setStatusFilter,
  patientSearch,
  setPatientSearch,
  stats,
  filtered,
  searchableTasks,
  taskSortMode,
  setTaskSortMode,
  sortKey,
  sortDirection,
  onSort,
  isExpandingTaskSearch,
  onExpandTaskSearchScope,
  onSearchTaskPatients,
  onClearTaskSearch,
  hasMoreTasks,
  isLoadingMoreTasks,
  onLoadMoreTasks,
  expandedPatients,
  setExpandedPatients,
  selectedTaskIds,
  setSelectedTaskIds,
  onBulkApply,
}) => {
  const safeStats = stats ?? DEFAULT_STATS;
  const safeOwnerStats = ownerStats ?? DEFAULT_OWNER_STATS;
  const safeFiltered = filtered ?? [];
  const safeSearchableTasks = searchableTasks ?? [];
  const safeOwnerFilter = ownerFilter || 'all';
  const safeActiveTeam = activeTeam || 'all';
  const safeStatusFilter = statusFilter || 'open';
  const hasPatientSearch = !!patientSearch;
  const [expandedLinkedTasks, setExpandedLinkedTasks] = useState({});
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);
  const clients = useCustomSelector((state) => getClients(state)) || [];
  const appointments = useCustomSelector(getDashboardV2Appointments) || [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!sortMenuRef.current?.contains(event.target)) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const appointmentsById = useMemo(
    () =>
      appointments.reduce((acc, appointment) => {
        const appointmentId = `${appointment?.id || ''}`.trim();
        if (!appointmentId) return acc;
        return { ...acc, [appointmentId]: appointment };
      }, {}),
    [appointments],
  );

  const patientSearchMetadataById = useMemo(
    () =>
      buildPatientSearchMetadataById({
        tasks: safeSearchableTasks,
        appointmentsById,
        clients,
      }),
    [appointmentsById, clients, safeSearchableTasks],
  );

  const taskSearchPlaceholder = useMemo(
    () => getTaskSearchPlaceholder(taskSortMode),
    [taskSortMode],
  );

  const taskSearchOptions = useMemo(
    () =>
      buildTaskSearchOptions({
        tasks: safeSearchableTasks,
        taskSortMode,
        patientSearchMetadataById,
      }),
    [patientSearchMetadataById, safeSearchableTasks, taskSortMode],
  );

  const patientGroups = useMemo(
    () =>
      buildPatientGroups({
        tasks: safeFiltered,
        appointmentsById,
        clients,
        preserveOrder: taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority,
      }),
    [appointmentsById, clients, safeFiltered, taskSortMode],
  );

  const reasonGroups = useMemo(() => buildReasonGroups(safeFiltered), [safeFiltered]);
  const priorityGroups = useMemo(() => buildPriorityGroups(safeFiltered), [safeFiltered]);

  const taskGroups = useMemo(() => {
    if (taskSortMode === TASK_QUEUE_SORT_MODE.ReasonPriority) return reasonGroups;
    if (taskSortMode === TASK_QUEUE_SORT_MODE.Priority) return priorityGroups;
    return patientGroups;
  }, [patientGroups, priorityGroups, reasonGroups, taskSortMode]);

  const prioritySummary = useMemo(
    () => buildTaskQueuePrioritySummary(safeFiltered),
    [safeFiltered],
  );

  const priorityBucketCountsByKey = null;

  const summaryLabel = useMemo(
    () => buildTaskQueueSummaryLabel(taskSortMode, taskGroups.length, safeFiltered.length),
    [safeFiltered.length, taskGroups.length, taskSortMode],
  );

  // Selection logic
  const allSelectableTaskIds = useMemo(() => safeFiltered.map((t) => `${t.id}`), [safeFiltered]);

  const selectedCount = useMemo(
    () => allSelectableTaskIds.filter((id) => selectedTaskIds.has(id)).length,
    [allSelectableTaskIds, selectedTaskIds],
  );

  const selectedBreakdown = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 };
    safeFiltered.forEach((task) => {
      if (selectedTaskIds.has(`${task.id}`)) {
        const p = task?.priority || 'low';
        if (counts[p] !== undefined) counts[p]++;
      }
    });
    return counts;
  }, [safeFiltered, selectedTaskIds]);

  const isAllSelected =
    allSelectableTaskIds.length > 0 && selectedCount === allSelectableTaskIds.length;
  const isIndeterminate = selectedCount > 0 && !isAllSelected;

  const [previewResult, setPreviewResult] = useState(null);

  const tasksById = useMemo(() => {
    const m = new Map();
    safeFiltered.forEach((task) => m.set(`${task.id}`, task));
    return m;
  }, [safeFiltered]);

  const loadByClientId = useMemo(
    () => computeMinutesLoadByClientId(safeSearchableTasks, clients, 'day'),
    [safeSearchableTasks, clients],
  );

  const openPreview = useCallback(() => {
    const selectedTasks = safeFiltered.filter((t) => selectedTaskIds.has(`${t.id}`));
    setPreviewResult(balanceAssign(selectedTasks, clients, loadByClientId, safeActiveTeam, 'day'));
  }, [safeFiltered, selectedTaskIds, clients, loadByClientId, safeActiveTeam]);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleSelectAll = useCallback(() => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (isAllSelected || isIndeterminate) {
        allSelectableTaskIds.forEach((id) => next.delete(id));
      } else {
        allSelectableTaskIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allSelectableTaskIds, isAllSelected, isIndeterminate, setSelectedTaskIds]);

  const handleToggleTask = useCallback(
    (taskId) => {
      const normalizedId = `${taskId}`;
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        if (next.has(normalizedId)) {
          next.delete(normalizedId);
        } else {
          next.add(normalizedId);
        }
        return next;
      });
    },
    [setSelectedTaskIds],
  );

  const handleToggleGroup = useCallback(
    (taskIds) => {
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        const allSelected = taskIds.every((id) => next.has(id));
        if (allSelected) {
          taskIds.forEach((id) => next.delete(id));
        } else {
          taskIds.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [setSelectedTaskIds],
  );

  const togglePatient = (groupKey) => {
    setExpandedPatients((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const expandAllPatients = () => {
    setExpandedPatients(taskGroups.reduce((acc, group) => ({ ...acc, [group.key]: true }), {}));
  };

  const collapseAllPatients = () => {
    setExpandedPatients({});
  };

  const toggleLinkedTasks = (taskId) => {
    const normalizedTaskId = `${taskId || ''}`;
    if (!normalizedTaskId) return;

    setExpandedLinkedTasks((prev) => ({
      ...prev,
      [normalizedTaskId]: !prev[normalizedTaskId],
    }));
  };

  const sortIcon = (key) => {
    const isActive = sortKey === key;
    return (
      <img
        className={taskViewStyles.sortIcon}
        style={{ transform: isActive && sortDirection === 'asc' ? 'rotate(-180deg)' : 'none' }}
        src={CommonIcons.sortDownIcon}
        alt={isActive ? `sort-${sortDirection}` : 'sort'}
      />
    );
  };

  const renderTaskRow = (group, task) => {
    const localDelegatedSubtasks =
      group.linkedTaskStructure.subtaskChildTasksByParentId.get(`${task.id}`) || [];
    const delegatedSubtaskMeta = getDelegatedSubtaskMeta(localDelegatedSubtasks);

    return (
      <TaskRow
        key={task.id}
        task={task}
        isLinkedChild={group.linkedTaskStructure.linkedChildIds.has(`${task.id}`)}
        isSubtaskChild={group.linkedTaskStructure.subtaskChildIds.has(`${task.id}`)}
        isSubtask={!!task?.parentTaskId}
        parentTitle={
          task?.parentTaskId
            ? (group.tasks.find(
                (candidateTask) => `${candidateTask?.id || ''}` === `${task.parentTaskId || ''}`,
              )?.title ?? null)
            : null
        }
        linkedParentTitle={getLinkedTaskWaitingForTitle(task, group.tasks) ?? null}
        linkedChildCount={group.linkedTaskStructure.parentLinkedChildCount.get(`${task.id}`) ?? 0}
        subtaskChildCount={group.linkedTaskStructure.parentSubtaskChildCount.get(`${task.id}`) ?? 0}
        delegatedSubtaskMeta={delegatedSubtaskMeta}
        linkedExpanded={!!expandedLinkedTasks[`${task.id}`]}
        onToggleLinked={toggleLinkedTasks}
        selectionMode
        isSelected={selectedTaskIds.has(`${task.id}`)}
        onToggleSelect={handleToggleTask}
      />
    );
  };

  const renderTaskItems = (group, items) =>
    items.map((item) => {
      if (item.type === 'child-group') {
        return (
          <div key={item.key} className={taskViewStyles.childGroupContainer}>
            {item.items.map((childItem) => {
              if (childItem.type === 'label') {
                return (
                  <div key={childItem.key} className={taskViewStyles.childSectionLabel}>
                    {childItem.label}
                  </div>
                );
              }

              if (childItem.type === 'child-group') {
                return renderTaskItems(group, [childItem])[0];
              }

              return renderTaskRow(group, childItem.task);
            })}
          </div>
        );
      }

      return renderTaskRow(group, item.task);
    });

  const renderTasksTable = (group, items, sectionTaskIds = []) => {
    const tableAllSelected =
      sectionTaskIds.length > 0 && sectionTaskIds.every((id) => selectedTaskIds.has(id));
    const tableIsIndeterminate =
      !tableAllSelected && sectionTaskIds.some((id) => selectedTaskIds.has(id));

    return (
      <div className={taskViewStyles.bucketRows}>
        <div className={taskViewStyles.tableHeader}>
          {sectionTaskIds.length > 0 ? (
            <input
              type="checkbox"
              className={styles.sectionCheckbox}
              checked={tableAllSelected}
              ref={(el) => {
                if (el) el.indeterminate = tableIsIndeterminate;
              }}
              onChange={() => handleToggleGroup(sectionTaskIds)}
              aria-label="Select all tasks in section"
            />
          ) : (
            <span />
          )}
          <span className={taskViewStyles.headerCell}>Task</span>
          <span className={taskViewStyles.headerCell} />
          <button
            type="button"
            onClick={() => onSort?.('status')}
            className={`${taskViewStyles.headerCell} ${taskViewStyles.sortButton} ${
              sortKey === 'status' ? taskViewStyles.sortActive : ''
            }`}
          >
            Status
            {sortIcon('status')}
          </button>
          <button
            type="button"
            onClick={() => onSort?.('priority')}
            className={`${taskViewStyles.headerCell} ${taskViewStyles.sortButton} ${
              sortKey === 'priority' ? taskViewStyles.sortActive : ''
            }`}
          >
            Priority
            {sortIcon('priority')}
          </button>
          <button
            type="button"
            onClick={() => onSort?.('due')}
            className={`${taskViewStyles.headerCell} ${taskViewStyles.sortButton} ${taskViewStyles.headerDue} ${
              sortKey === 'due' ? taskViewStyles.sortActive : ''
            }`}
          >
            Due
            {sortIcon('due')}
          </button>
        </div>
        {renderTaskItems(group, items)}
      </div>
    );
  };

  const renderPatientGroup = (group) => {
    const isExpanded = !!expandedPatients[group.key];
    const pathway = pathwayLabel[group.pathway] ?? group.pathway;

    return (
      <div key={group.key} className={taskViewStyles.patientGroup}>
        <button
          type="button"
          className={taskViewStyles.patientRow}
          onClick={() => togglePatient(group.key)}
        >
          <ChevronDown
            size={12}
            strokeWidth={1.8}
            className={`${taskViewStyles.patientChevron} ${
              !isExpanded ? taskViewStyles.patientChevronCollapsed : ''
            }`}
          />
          <span className={taskViewStyles.patientMarker} />
          <div className={taskViewStyles.patientMain}>
            <div className={taskViewStyles.patientTopRow}>
              <span className={taskViewStyles.patientName}>{group.patientName}</span>
              {group.patientMrn ? (
                <span className={taskViewStyles.patientMetaMuted}>{group.patientMrn}</span>
              ) : null}
            </div>
            <div className={taskViewStyles.patientMetaRow}>
              {group.patientPhase != null ? (
                <span className={taskViewStyles.patientMetaText}>Phase {group.patientPhase}</span>
              ) : null}
              {group.patientClinician ? (
                <span className={taskViewStyles.patientMetaText}>{group.patientClinician}</span>
              ) : null}
              {pathway ? (
                <Badge variant="outline" className={taskViewStyles.pathwayBadge}>
                  {pathway}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className={taskViewStyles.patientPills}>
            {group.criticalCount ? (
              <Badge variant="critical" className={taskViewStyles.patientSummaryBadge}>
                {group.criticalCount} critical
              </Badge>
            ) : null}
            {group.blockedCount ? (
              <Badge variant="critical" className={taskViewStyles.patientSummaryBadge}>
                {group.blockedCount} blocked
              </Badge>
            ) : null}
            {group.escalatedCount ? (
              <Badge variant="escalated" className={taskViewStyles.patientSummaryBadge}>
                {group.escalatedCount} escalated
              </Badge>
            ) : null}
            {group.nowCount || group.laterCount ? (
              <span className={taskViewStyles.summaryPillGroup}>
                {group.nowCount ? (
                  <span
                    className={`${taskViewStyles.summaryPill} ${taskViewStyles.summaryPillDark} ${
                      group.laterCount ? taskViewStyles.summaryPillGrouped : ''
                    }`}
                  >
                    {group.nowCount} now
                  </span>
                ) : null}
                {group.laterCount ? (
                  <span
                    className={`${taskViewStyles.summaryPill} ${
                      group.nowCount
                        ? `${taskViewStyles.summaryPillGrouped} ${taskViewStyles.summaryPillGroupedTrailing}`
                        : ''
                    }`}
                  >
                    {group.laterCount} later
                  </span>
                ) : null}
              </span>
            ) : null}
          </div>
          <span className={taskViewStyles.patientDue}>{group.nextDueLabel}</span>
        </button>

        {isExpanded ? (
          <div className={taskViewStyles.patientExpanded}>
            {[
              {
                key: 'priority',
                label: 'Priority',
                rawTasks: group.priorityTasks,
                tasks: buildRenderableSectionTasks(
                  group.priorityTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
                items: buildRenderableSectionItems(
                  group.priorityTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
              },
              {
                key: 'future',
                label: 'Future',
                rawTasks: group.futureTasks,
                tasks: buildRenderableSectionTasks(
                  group.futureTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
                items: buildRenderableSectionItems(
                  group.futureTasks,
                  group.linkedTaskStructure,
                  expandedLinkedTasks,
                ),
              },
            ].map((section, index) => {
              if (!section.tasks.length) return null;

              const sectionTaskIds = (() => {
                const ids = new Set();
                section.rawTasks.forEach((task) => {
                  const taskId = `${task.id}`;
                  ids.add(taskId);
                  [
                    ...(group.linkedTaskStructure.subtaskChildTasksByParentId.get(taskId) || []),
                    ...(group.linkedTaskStructure.linkedChildTasksByParentId.get(taskId) || []),
                  ].forEach((child) => ids.add(`${child.id}`));
                });
                return [...ids];
              })();

              return (
                <div key={section.key} className={taskViewStyles.bucketSection}>
                  <div className={taskViewStyles.bucketHeader}>
                    <span
                      className={`${taskViewStyles.bucketIndex} ${
                        section.key === 'priority' ? taskViewStyles.bucketIndexDark : ''
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className={taskViewStyles.bucketLabel}>{section.label}</span>
                    <span className={taskViewStyles.bucketCount}>{section.tasks.length}</span>
                  </div>
                  {renderTasksTable(group, section.items, sectionTaskIds)}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };

  const renderBucketGroup = (group) => {
    const isExpanded = !!expandedPatients[group.key];
    const renderableTasks = buildRenderableSectionTasks(
      group.tasks,
      group.linkedTaskStructure,
      expandedLinkedTasks,
    );
    const renderableItems = buildRenderableSectionItems(
      group.tasks,
      group.linkedTaskStructure,
      expandedLinkedTasks,
    );
    const isPriorityBucket = group.bucketType === 'priority';
    const priorityKey = group.key.replace('priority-', '');
    const displayCount =
      isPriorityBucket && priorityBucketCountsByKey
        ? (priorityBucketCountsByKey[priorityKey] ?? renderableTasks.length)
        : renderableTasks.length;

    return (
      <div key={group.key} className={taskViewStyles.patientGroup}>
        <button
          type="button"
          className={taskViewStyles.queueGroupRow}
          onClick={() => togglePatient(group.key)}
        >
          <ChevronDown
            size={12}
            strokeWidth={1.8}
            className={`${taskViewStyles.patientChevron} ${
              !isExpanded ? taskViewStyles.patientChevronCollapsed : ''
            }`}
          />
          <div className={taskViewStyles.queueGroupMain}>
            <div className={taskViewStyles.patientTopRow}>
              <Badge
                variant={isPriorityBucket ? group.badgeVariant || 'outline' : 'outline'}
                className={taskViewStyles.patientSummaryBadge}
              >
                {group.title}
              </Badge>
              <span className={taskViewStyles.patientMetaMuted}>{displayCount}</span>
            </div>
          </div>
        </button>

        {isExpanded ? (
          <div className={taskViewStyles.patientExpanded}>
            <div className={taskViewStyles.bucketSection}>
              <div className={taskViewStyles.bucketHeader}>
                <span className={`${taskViewStyles.bucketIndex} ${taskViewStyles.bucketIndexDark}`}>
                  1
                </span>
                <span className={taskViewStyles.bucketLabel}>Tasks</span>
                <span className={taskViewStyles.bucketCount}>{displayCount}</span>
              </div>
              {renderTasksTable(
                group,
                renderableItems,
                group.tasks.map((t) => `${t.id}`),
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const ownerTabs = TASK_QUEUE_OWNER_TABS.map((tab) => ({
    ...tab,
    count: safeOwnerStats[tab.key] ?? 0,
  }));

  const teamTabs = TASK_QUEUE_TEAM_TABS.map((tab) => ({
    ...tab,
    count: tab.key === 'all' ? safeStats.total : (safeStats[tab.key] ?? 0),
    icon: TEAM_TAB_ICON_BY_KEY[tab.key],
  }));

  const statusTabs = TASK_QUEUE_STATUS_TABS.map((tab) => ({
    ...tab,
    label: tab.key === 'open' ? tab.label : `${tab.label} ${safeStats[tab.key] ?? 0}`,
  }));

  const activeSortModeLabel =
    TASK_QUEUE_SORT_MODE_OPTIONS.find((option) => option.value === taskSortMode)?.label ||
    'Member + Priority';

  return (
    <>
      <div className={styles.viewHeader}>
        <ListChecks className={styles.viewHeaderIcon} strokeWidth={1.8} />
        <span className={styles.viewHeaderTitle}>Bulk assign</span>
        <span className={styles.viewHeaderDescription}>
          Select tasks, then balance them across staff by workload.
        </span>
      </div>

      <div className={taskViewStyles.scopeBar}>
        <ScopeFilterGroup
          departments={departments}
          filters={scopeFilters}
          onChangeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          showUtilities={showScopeUtilities}
          isPinned={isPinnedToCurrentScope}
          onTogglePin={onToggleScopePin}
          canReset={canResetToPinnedScope}
          onReset={onResetScopeToPinned}
          isPaused={isScopePaused}
          onTogglePause={onToggleScopePause}
        />
      </div>

      <div className={taskViewStyles.teamTabsBar}>
        <div className={taskViewStyles.teamTabsGroup}>
          {teamTabs.map((team) => (
            <button
              key={team.key}
              type="button"
              onClick={() => setActiveTeam(team.key)}
              className={`${taskViewStyles.teamTabButton} ${
                safeActiveTeam === team.key ? taskViewStyles.teamTabActive : ''
              }`}
            >
              <span className={taskViewStyles.teamTabIcon}>
                <team.icon size={14} strokeWidth={1.9} aria-hidden="true" />
              </span>
              {team.label}
              <span className={taskViewStyles.teamTabCount}>{team.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={taskViewStyles.filters}>
        <div className={taskViewStyles.filterRow}>
          <div className={taskViewStyles.filterGroup}>
            {ownerTabs.map((ownerTab) => (
              <button
                key={ownerTab.key}
                type="button"
                onClick={() => setOwnerFilter(ownerTab.key)}
                className={`${taskViewStyles.filterButton} ${
                  safeOwnerFilter === ownerTab.key ? taskViewStyles.filterActive : ''
                }`}
              >
                {ownerTab.label}
                <span className={taskViewStyles.filterCount}>{ownerTab.count}</span>
              </button>
            ))}
          </div>
          <div className={taskViewStyles.filterDivider} />
          <div className={taskViewStyles.filterGroup}>
            {statusTabs.map((status) => (
              <button
                key={status.key}
                type="button"
                onClick={() => setStatusFilter(status.key)}
                className={`${taskViewStyles.filterButton} ${
                  safeStatusFilter === status.key ? taskViewStyles.filterActive : ''
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
          <div className={taskViewStyles.filterActions}>
            <PatientTaskSearch
              patientSearch={patientSearch}
              setPatientSearch={setPatientSearch}
              getPatientSearchLabel={(selection) =>
                getTaskSearchSelectionLabel(selection, patientSearchMetadataById)
              }
              getPatientSearchPathway={getPatientSearchPathway}
              patientSearchMetadataById={patientSearchMetadataById}
              searchOptions={taskSearchOptions}
              searchPlaceholder={taskSearchPlaceholder}
              isSearchLoading={isExpandingTaskSearch}
              onExpandSearchScope={onExpandTaskSearchScope}
              onSearchPatients={
                taskSortMode === TASK_QUEUE_SORT_MODE.PatientPriority
                  ? onSearchTaskPatients
                  : undefined
              }
              onClearSearch={onClearTaskSearch}
            />
            <button
              type="button"
              onClick={expandAllPatients}
              className={taskViewStyles.filterActionButton}
              disabled={!taskGroups.length}
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAllPatients}
              className={taskViewStyles.filterActionButton}
              disabled={!taskGroups.length}
            >
              Collapse
            </button>
          </div>
        </div>
      </div>

      <div className={taskViewStyles.layout}>
        <div className={taskViewStyles.listCard}>
          <div className={taskViewStyles.summaryBar}>
            <div className={taskViewStyles.summaryMeta}>
              <span className={taskViewStyles.summaryText}>
                {summaryLabel}
                {selectedCount > 0 ? ` · ${selectedCount} selected` : null}
              </span>
              <div className={styles.countsRow}>
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  id="bulk-assign-select-all"
                  className={styles.selectAllCheckbox}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={!allSelectableTaskIds.length}
                  aria-label="Select all visible tasks"
                />
                <div className={taskViewStyles.prioritySummary}>
                  <span
                    className={`${taskViewStyles.prioritySummaryItem} ${taskViewStyles.prioritySummaryCritical}`}
                  >
                    Critical: {prioritySummary.critical}
                  </span>
                  <span className={taskViewStyles.prioritySummaryDivider}>·</span>
                  <span
                    className={`${taskViewStyles.prioritySummaryItem} ${taskViewStyles.prioritySummaryHigh}`}
                  >
                    High: {prioritySummary.high}
                  </span>
                  <span className={taskViewStyles.prioritySummaryDivider}>·</span>
                  <span
                    className={`${taskViewStyles.prioritySummaryItem} ${taskViewStyles.prioritySummaryMedium}`}
                  >
                    Medium: {prioritySummary.medium}
                  </span>
                  <span className={taskViewStyles.prioritySummaryDivider}>·</span>
                  <span
                    className={`${taskViewStyles.prioritySummaryItem} ${taskViewStyles.prioritySummaryLow}`}
                  >
                    Low: {prioritySummary.low}
                  </span>
                </div>
              </div>
              {selectedCount > 0 ? (
                <div className={styles.selectedBreakdownRow}>
                  <span className={styles.selectedBreakdownLabel}>Selected —</span>
                  <span
                    className={`${styles.selectedBreakdownItem} ${styles.selectedBreakdownCritical}`}
                  >
                    Crit {selectedBreakdown.critical}
                  </span>
                  <span className={styles.selectedBreakdownDivider}>·</span>
                  <span
                    className={`${styles.selectedBreakdownItem} ${styles.selectedBreakdownHigh}`}
                  >
                    High {selectedBreakdown.high}
                  </span>
                  <span className={styles.selectedBreakdownDivider}>·</span>
                  <span
                    className={`${styles.selectedBreakdownItem} ${styles.selectedBreakdownMedium}`}
                  >
                    Med {selectedBreakdown.medium}
                  </span>
                  <span className={styles.selectedBreakdownDivider}>·</span>
                  <span
                    className={`${styles.selectedBreakdownItem} ${styles.selectedBreakdownLow}`}
                  >
                    Low {selectedBreakdown.low}
                  </span>
                </div>
              ) : null}
            </div>
            <div className={taskViewStyles.summaryActions}>
              <div ref={sortMenuRef} className={taskViewStyles.summarySortMenu}>
                <button
                  type="button"
                  className={taskViewStyles.summarySortTrigger}
                  onClick={() => setIsSortMenuOpen((prev) => !prev)}
                >
                  Sorted by {activeSortModeLabel.toLowerCase()}
                  <ChevronDown
                    size={12}
                    strokeWidth={1.8}
                    className={`${taskViewStyles.summarySortChevron} ${
                      isSortMenuOpen ? taskViewStyles.summarySortChevronOpen : ''
                    }`}
                  />
                </button>
                {isSortMenuOpen ? (
                  <div className={taskViewStyles.summarySortDropdown}>
                    {TASK_QUEUE_SORT_MODE_OPTIONS.map((option) => {
                      const isSelected = option.value === taskSortMode;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          className={taskViewStyles.summarySortOption}
                          onClick={() => {
                            setTaskSortMode?.(option.value);
                            setIsSortMenuOpen(false);
                          }}
                        >
                          <Check
                            size={12}
                            strokeWidth={2}
                            className={`${taskViewStyles.summarySortCheck} ${
                              isSelected ? taskViewStyles.summarySortCheckVisible : ''
                            }`}
                          />
                          <span>{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {isLoading && (
            <div className={taskViewStyles.loaderWrap}>
              <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={12} />
            </div>
          )}

          {!isLoading &&
            !taskGroups.length &&
            (hasPatientSearch ? (
              <div className={taskViewStyles.emptyState}>No tasks match filters</div>
            ) : (
              <div className={taskViewStyles.emptyState}>No tasks available.</div>
            ))}

          {!isLoading &&
            taskGroups.map((group) =>
              group.kind === 'patient' ? renderPatientGroup(group) : renderBucketGroup(group),
            )}

          {!isLoading && hasMoreTasks ? (
            <div className={taskViewStyles.loadMoreWrap}>
              <button
                type="button"
                className={taskViewStyles.loadMoreButton}
                onClick={() => onLoadMoreTasks?.()}
                disabled={isLoadingMoreTasks || !onLoadMoreTasks}
              >
                {isLoadingMoreTasks ? 'Loading more tasks...' : 'Load more tasks'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {selectedCount > 0 ? (
        <div className={styles.floatingBar}>
          <div className={styles.floatingBarInner}>
            <span className={styles.floatingBarLabel}>
              {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'} selected
            </span>
            <button type="button" className={styles.floatingBarPrimary} onClick={openPreview}>
              Balance &amp; Assign
            </button>
            <button
              type="button"
              className={styles.floatingBarGhost}
              onClick={() => setSelectedTaskIds(new Set())}
            >
              Clear selection
            </button>
          </div>
        </div>
      ) : null}

      <BulkAssignPreview
        open={previewResult !== null}
        onClose={() => setPreviewResult(null)}
        result={previewResult ?? { proposals: [], unassignableTaskIds: [] }}
        clients={clients}
        allTasks={safeSearchableTasks}
        tasksById={tasksById}
        activeTeam={safeActiveTeam}
        onApply={(proposals) => {
          onBulkApply?.(proposals);
          setPreviewResult(null);
          setSelectedTaskIds(new Set());
        }}
      />
    </>
  );
};

BulkAssignView.propTypes = {
  isLoading: PropTypes.bool,
  departments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      regionName: PropTypes.string,
      countyName: PropTypes.string,
      marketName: PropTypes.string,
    }),
  ),
  scopeFilters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    counties: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    departments: PropTypes.arrayOf(PropTypes.string),
  }),
  onChangeScopeFilters: PropTypes.func,
  guideEnabled: PropTypes.bool,
  onToggleGuide: PropTypes.func,
  isScopePaused: PropTypes.bool,
  onToggleScopePause: PropTypes.func,
  isPinnedToCurrentScope: PropTypes.bool,
  canResetToPinnedScope: PropTypes.bool,
  showScopeUtilities: PropTypes.bool,
  onToggleScopePin: PropTypes.func,
  onResetScopeToPinned: PropTypes.func,
  ownerFilter: PropTypes.oneOf(['all', 'my', 'following', 'unassigned']),
  setOwnerFilter: PropTypes.func,
  ownerStats: PropTypes.shape({
    all: PropTypes.number,
    my: PropTypes.number,
    following: PropTypes.number,
    unassigned: PropTypes.number,
  }),
  activeTeam: PropTypes.oneOf(['all', 'member', 'ops', 'clinical', 'rcm']),
  setActiveTeam: PropTypes.func,
  statusFilter: PropTypes.oneOf(TASK_STATUS_FILTER_KEYS),
  setStatusFilter: PropTypes.func,
  patientSearch: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    preferredName: PropTypes.string,
  }),
  setPatientSearch: PropTypes.func,
  stats: PropTypes.shape({
    total: PropTypes.number,
    member: PropTypes.number,
    ops: PropTypes.number,
    clinical: PropTypes.number,
    rcm: PropTypes.number,
  }),
  filtered: PropTypes.arrayOf(PropTypes.object),
  searchableTasks: PropTypes.arrayOf(PropTypes.object),
  taskSortMode: PropTypes.number,
  setTaskSortMode: PropTypes.func,
  sortKey: PropTypes.oneOf(['due', 'status', 'priority']),
  sortDirection: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  isExpandingTaskSearch: PropTypes.bool,
  onExpandTaskSearchScope: PropTypes.func,
  onSearchTaskPatients: PropTypes.func,
  onClearTaskSearch: PropTypes.func,
  hasMoreTasks: PropTypes.bool,
  isLoadingMoreTasks: PropTypes.bool,
  onLoadMoreTasks: PropTypes.func,
  expandedPatients: PropTypes.objectOf(PropTypes.bool),
  setExpandedPatients: PropTypes.func,
  selectedTaskIds: PropTypes.instanceOf(Set),
  setSelectedTaskIds: PropTypes.func,
  onBulkApply: PropTypes.func,
};

export default BulkAssignView;
